export type PersistedTaskStatus = "pending" | "done" | "skipped";
export type TaskLifecycle = "pending" | "doing" | "done" | "skipped";
export type ExecutionControlState = "start" | "doing" | "done";

export type TaskLifecycleSource = {
  status: PersistedTaskStatus;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
};

export const TASK_LIFECYCLE_ORDER: TaskLifecycle[] = [
  "pending",
  "doing",
  "done",
  "skipped",
];

export const TASK_LIFECYCLE_LABELS: Record<TaskLifecycle, string> = {
  pending: "Pending",
  doing: "Started",
  done: "Done",
  skipped: "Skipped",
};

export const EXECUTION_CONTROL_LABELS: Record<ExecutionControlState, string> = {
  start: "Ready",
  doing: "Started",
  done: "Done",
};

export function getTaskLifecycleLabel(lifecycle: TaskLifecycle) {
  return TASK_LIFECYCLE_LABELS[lifecycle];
}

export function getExecutionControlLabel(state: ExecutionControlState) {
  return EXECUTION_CONTROL_LABELS[state];
}

export function deriveTaskLifecycle(task: TaskLifecycleSource): TaskLifecycle {
  if (task.status === "pending") return "pending";
  if (task.status === "skipped") return "skipped";
  return task.completedAt ? "done" : "doing";
}

export function getExecutionControlState(
  task: Pick<TaskLifecycleSource, "startedAt" | "completedAt">,
): ExecutionControlState {
  if (task.completedAt) return "done";
  if (task.startedAt) return "doing";
  return "start";
}

export function getExecutionSwipeLabel(
  task: Pick<TaskLifecycleSource, "startedAt" | "completedAt">,
) {
  return getExecutionControlState(task) === "start" ? "Started" : "Done";
}

export function getSecondaryStatusSwipe(
  task: Pick<TaskLifecycleSource, "status">,
): { status: "pending" | "skipped"; label: "Pending" | "Skipped" } {
  return task.status === "skipped"
    ? { status: "pending", label: "Pending" }
    : { status: "skipped", label: "Skipped" };
}

export function resetTaskStatusTiming(status: "pending" | "skipped") {
  return {
    status,
    startedAt: null,
    completedAt: null,
  } as const;
}

export function advanceTaskExecution<T extends TaskLifecycleSource>(
  task: T,
  when: Date,
): { status: "done"; startedAt: Date } | { status: "done"; completedAt: Date } {
  if (!task.startedAt) {
    return {
      status: "done",
      startedAt: when,
    };
  }

  return {
    status: "done",
    completedAt: when,
  };
}

export function resolveTaskExecutionTimestamp(
  task: Pick<TaskLifecycleSource, "startedAt">,
  now: Date,
  scheduledStart?: Date | null,
) {
  return !task.startedAt && scheduledStart ? scheduledStart : now;
}

export function taskLifecycleRank(lifecycle: TaskLifecycle) {
  return TASK_LIFECYCLE_ORDER.indexOf(lifecycle);
}
